import { Capacitor } from '@capacitor/core';

import { environment } from '@ninety/mobile/platform/environments';

import { Provider } from '../model/tokens';

/**
 * The two ends of the browser leg: the URL an external user-agent is opened with, and the callback
 * it comes back on.
 *
 * Both are pure functions over configuration, so the rules that matter here — a code and never a
 * token, S256 and never `plain`, and which deep links count as a callback at all — are testable by
 * comparing strings. The handler that calls them is where the PKCE material is generated; see
 * `pkce.ts` for why no component is allowed to supply its own.
 */

/**
 * The callback the native build receives.
 *
 * `ninetymobile://` is an unclaimed custom scheme: any app on the device can register it and receive
 * this callback. PKCE is what makes that acceptable — see `pkce.ts`.
 */
export const NATIVE_REDIRECT_URI = 'ninetymobile://login/';

/**
 * The callback the web build receives — the sign-in route it is already served from.
 *
 * `location.origin` rather than a configured protocol against `location.host`, because the origin
 * cannot disagree with how the page was actually served.
 *
 * The trailing slash is the spelling the pools register for this app, as they do for
 * {@link NATIVE_REDIRECT_URI} and every other property.
 *
 * Recognising a callback is unaffected either way — {@link authorizationResponse} compares paths
 * without their trailing slash, because which spelling the OS delivers is the OS's business.
 *
 * Shared with `amplifyConfiguration`'s `redirectSignIn` so the two cannot drift into disagreeing
 * about what this app's web callback is.
 */
export function webRedirectUri(): string {
  return `${window.location.origin}/login/`;
}

/**
 * Where the Authorization Server sends the response, on this platform.
 *
 * **One function, called by both legs**: the token
 * endpoint requires the same `redirect_uri` the authorization request used, and answers a mismatch
 * with `invalid_grant` — indistinguishable from a spent code. On web the two legs run in *different
 * pages*, so the value cannot be remembered between them and has to be derived the same way twice.
 *
 * Whichever it answers must be on the Cognito app client's callback URL allow-list, or the
 * authorization request is refused before anyone sees a provider.
 */
export function authorizationRedirectUri(): string {
  return Capacitor.isNativePlatform() ? NATIVE_REDIRECT_URI : webRedirectUri();
}

/**
 * The scopes a Grant asks for.
 *
 * Shared with `amplifyConfiguration` so a federated session is never quietly narrower than a
 * password one. `aws.cognito.signin.user.admin` is in the set because Amplify needs it for the
 * user-pool operations it performs against an access token it holds.
 */
export const AUTHORIZATION_SCOPES = ['phone', 'email', 'openid', 'profile', 'aws.cognito.signin.user.admin'] as const;

/**
 * How Cognito names a federated identity provider.
 *
 * Naming it in the authorization request skips Cognito's own provider chooser, which is a screen
 * nobody asked for after they have already tapped "Continue with Google".
 *
 * Microsoft throws rather than guessing a name: it is **not** federated into this user pool — the
 * pool has the Cognito directory, Facebook and Google — so there is no `identity_provider` value
 * that would work. It never reaches here, because {@link authorizationUrl} sends it to Azure
 * instead; the throw is what keeps that true if someone routes it through Cognito by mistake.
 */
export function cognitoIdentityProvider(provider: Provider): string {
  switch (provider) {
    case 'google':
      return 'Google';
    case 'microsoft':
      throw new Error('microsoft is not federated into the Cognito user pool');
  }
}

/**
 * Where Azure sends the Microsoft response.
 *
 * **Not a value we are free to choose.** The Resource Server redeems this code, and RFC 6749 §4.1.3
 * makes it send the same `redirect_uri` the authorization request used — which it rebuilds as
 * `${origin}/login` from the `ninety-origin` header on the `/Login` call. So this has to agree with
 * `environment.nativeHostName` on native, and the two only line up on the **production** build.
 * On Dev1 they disagree (`http://localhost:4200` against `https://app.dev1.90srv.com`) and Azure
 * answers `invalid_grant`, so Microsoft sign-in is prod-build-only until the Resource Server takes
 * the redirect from the request instead of the header. Recorded in the spec's open questions.
 *
 * An `https://` callback rather than the custom scheme the Cognito leg uses, and it needs no
 * interception to come back: `mobile.ninety.io` is a claimed deep link on both platforms —
 * `applinks:` in the iOS entitlements, an `autoVerify` intent filter in the Android manifest — so
 * the OS hands the redirect to the app as an `appUrlOpen` while it is still in the external agent.
 * That is strictly better than `ninetymobile://`, which any app on the device may also register.
 */
export function microsoftRedirectUri(): string {
  return `${environment.microsoftLoginRedirectUri}/login`;
}

/**
 * The Azure authorization endpoint URL for a Microsoft Grant.
 *
 * **No `code_challenge`, deliberately.** Azure demands a `code_verifier` at redemption from any
 * request that carried a challenge, and the redemption happens at the Resource Server, whose
 * `/Login` contract has no field that could carry one. Sending a challenge we cannot complete would
 * turn every Microsoft sign-in into an `invalid_grant`. The nonce below is unaffected — `state` is
 * ours end to end, echoed back by Azure and compared in `transition()` — so this leg gets the CSRF
 * protection and the replay bound without the interception protection.
 *
 * `prompt=select_account` is what makes Azure ask which account to use. Without it Azure signs
 * straight in as whoever the device is already signed in as, which on a shared or work device is the
 * wrong person and no way to say so.
 */
function microsoftAuthorizationUrl(nonce: string): string {
  const params = new URLSearchParams({
    client_id: environment.microsoftClientId,
    response_type: 'code',
    redirect_uri: microsoftRedirectUri(),
    response_mode: 'query',
    scope: 'openid profile email',
    prompt: 'select_account',
    state: nonce,
  });

  // The configured value already ends in `?`, which is why this concatenates rather than joining.
  return `${environment.microsoftAuthUrl}${params.toString()}`;
}

/** The public half of the PKCE material, which is all the authorization request may carry. */
export interface AuthorizationChallenge {
  /** Absent on the Microsoft leg, which cannot use PKCE — see {@link microsoftAuthorizationUrl}. */
  readonly codeChallenge?: string;
  /** The `state` nonce. Named for the parameter it becomes, since that is what comes back. */
  readonly state: string;
}

/**
 * The authorization endpoint URL for a federated Grant.
 *
 * Two authorization servers, because there are two: Google is federated into the Cognito user pool
 * and goes through it, Microsoft is not and goes to Azure directly. That split is the whole of what
 * "bespoke Azure path" means, and it ends at the Token Exchange.
 */
export function authorizationUrl(provider: Provider, challenge: AuthorizationChallenge): string {
  if (provider === 'microsoft') {
    return microsoftAuthorizationUrl(challenge.state);
  }

  return cognitoAuthorizationUrl(provider, challenge);
}

/**
 * The Cognito authorization endpoint URL, for the providers the user pool federates.
 *
 * `URLSearchParams` rather than hand-built concatenation: it percent-encodes every value, and the
 * one that matters is `redirect_uri`, whose `://` and `/` would otherwise be ambiguous to Cognito.
 */
function cognitoAuthorizationUrl(provider: Provider, challenge: AuthorizationChallenge): string {
  if (challenge.codeChallenge === undefined) {
    // Unreachable from the handler, which always generates one for this leg. Refused rather than
    // sent without: Cognito accepts a request with no challenge and issues a code that needs no
    // verifier, which silently leaves an intercepted code redeemable by whoever intercepted it.
    throw new Error('a Cognito authorization request must carry a code challenge');
  }

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: environment.userPoolWebClientId,
    redirect_uri: authorizationRedirectUri(),
    identity_provider: cognitoIdentityProvider(provider),
    scope: AUTHORIZATION_SCOPES.join(' '),
    code_challenge_method: 'S256',
    code_challenge: challenge.codeChallenge,
    state: challenge.state,
  });

  return `https://${environment.cognitoDomain}/oauth2/authorize?${params.toString()}`;
}

/**
 * The parameters of an authorization callback, or `null` if this URL is not one.
 *
 * Nothing in the returned parameters is trusted — the machine compares `state` against the nonce it
 * generated, never against anything in here. What this function decides is narrower: whether a deep
 * link is a callback at all. The scheme also carries push
 * notifications, and reading one of those as a callback would hand the machine a response with no
 * nonce, which it reports as `auth.security_event` — a signal that is only useful because it cannot
 * be produced by a notification tap.
 *
 * The path is compared without its trailing slash, because the two spellings are the same URL and
 * which one arrives is the OS's business.
 *
 * Used for both platforms: on native the argument is a deep link, and on web it is
 * `window.location.href` at launch, which is how a returning redirect is recognised.
 *
 * ## Two callbacks, held to different standards
 *
 * The Cognito leg comes back on `ninetymobile://login/`, a place nothing else has any reason to
 * link to, so arriving there at all is the claim that this is a callback — and a bare one is the
 * security event it looks like.
 *
 * Microsoft comes back on `https://mobile.ninety.io/login`, which is also a **real page** people
 * reach from a bookmark or an email, and which the OS hands to the app as a claimed deep link
 * either way. Arriving there is therefore not a claim about anything, so it counts as a callback
 * only when it carries what a callback carries: a `code`, or an `error` saying why there is none.
 * Without that test a tap on a sign-in link from an email would be read as an authorization
 * response with no nonce — and on a device mid-Grant that is `auth.security_event`.
 *
 * **On the production web build the two addresses are the same place**, since the app is served
 * from `mobile.ninety.io` and `webRedirectUri()` resolves to `https://mobile.ninety.io/login/`. The
 * Cognito test above matches first and the stricter one below never runs. That is not a hole this
 * change opens: a browser at `/login` has always been the web build's own callback, and a launch
 * there resumes nothing without a stored Grant, which {@link takePendingAuthorization} supplies
 * once and only to the tab that started one. The strict test is what protects the **native** builds,
 * where the two addresses are genuinely different and a mail app can send someone to one of them.
 */
export function authorizationResponse(url: string): URLSearchParams | null {
  let candidate: URL;
  try {
    candidate = new URL(url);
  } catch {
    // Not a URL at all. A fact to handle, not an exception to propagate: this runs on every deep
    // link the OS delivers, and one malformed link must not take the listener down with it.
    return null;
  }

  if (isSamePlace(candidate, authorizationRedirectUri())) {
    return candidate.searchParams;
  }

  const carriesAResponse = candidate.searchParams.has('code') || candidate.searchParams.has('error');
  if (carriesAResponse && isSamePlace(candidate, microsoftRedirectUri())) {
    return candidate.searchParams;
  }

  return null;
}

function isSamePlace(candidate: URL, callbackUri: string): boolean {
  const callback = new URL(callbackUri);

  return (
    candidate.protocol === callback.protocol &&
    candidate.host === callback.host &&
    withoutTrailingSlash(candidate.pathname) === withoutTrailingSlash(callback.pathname)
  );
}

function withoutTrailingSlash(pathname: string): string {
  return pathname.replace(/\/$/, '');
}

/**
 * The error codes the authorization endpoint may answer with — RFC 6749 §4.1.2.1.
 *
 * A closed union so the code stays a groupable facet in Datadog: an unrecognised value maps to
 * `unrecognised` rather than travelling as itself, because a value from an identity provider is a
 * string we do not control, and an unbounded facet breaks the dashboard that reads it.
 */
export type AuthorizationErrorCode =
  | 'access_denied'
  | 'invalid_request'
  | 'invalid_scope'
  | 'server_error'
  | 'temporarily_unavailable'
  | 'unauthorized_client'
  | 'unsupported_response_type'
  /** Present, and not one of the above. The description is what to read in that case. */
  | 'unrecognised';

const AUTHORIZATION_ERROR_CODES: readonly AuthorizationErrorCode[] = [
  'access_denied',
  'invalid_request',
  'invalid_scope',
  'server_error',
  'temporarily_unavailable',
  'unauthorized_client',
  'unsupported_response_type',
];

/**
 * How much of an identity provider's message is kept.
 *
 * A bound rather than trust: the text is written by whoever we federated with, and it travels to
 * Datadog, where an unbounded field costs storage on every row that carries one.
 */
export const ERROR_DESCRIPTION_LIMIT = 200;

/**
 * What a failed authorization callback is safe to say about itself.
 *
 * **An allow-list of four fields, and never a deny-list.** The callback also carries the
 * authorization code and the `state` nonce, and a deny-list would ship any parameter Cognito adds
 * later by default. So the fields are named here, one at a time, and everything else is dropped —
 * including the URL itself.
 */
export interface AuthorizationErrorSummary {
  readonly code: AuthorizationErrorCode;
  /**
   * The provider's own `error_description`, truncated.
   *
   * The one free-form value anywhere on the path to telemetry, and it is here on purpose: a Cognito
   * federation failure says what it needs — `invalid_request: Attributes required: [email]` — and
   * without it the code alone sends someone to the AWS console to guess. It is the provider's message
   * about its own refusal, never a parameter of ours.
   */
  readonly description?: string;
  /** Whether a code was present at all. A boolean, because its value must never be logged. */
  readonly hasCode: boolean;
  readonly hasState: boolean;
}

/**
 * Summarise a callback that reported an error, or `null` if it did not report one.
 *
 * There is no summary of a callback that worked, so there is no log line carrying one.
 */
export function authorizationErrorSummary(params: URLSearchParams): AuthorizationErrorSummary | null {
  const error = params.get('error');
  if (!error) return null;

  const description = params.get('error_description');

  return {
    code: AUTHORIZATION_ERROR_CODES.find(code => code === error) ?? 'unrecognised',
    ...(description ? { description: description.slice(0, ERROR_DESCRIPTION_LIMIT) } : {}),
    hasCode: params.has('code'),
    hasState: params.has('state'),
  };
}
